import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Building2, 
  Shield, 
  Zap, 
  FileText, 
  Users, 
  Star,
  CheckCircle,
  ArrowRight,
  Package,
  BarChart3,
  Loader2
} from "lucide-react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/useAuth";

interface LoginPageProps {
  forceLoading?: boolean;
  loadingMessage?: string;
  progress?: number;
}

export function LoginPage({ forceLoading = false, loadingMessage: externalLoadingMessage, progress: externalProgress }: LoginPageProps = {}) {
  const { oauthLogin, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  // 检查是否在home页面
  const isHomePage = typeof window !== 'undefined' && 
    (window.location.pathname === '/' || window.location.pathname === '/myhome');

  // 查询项目数据 - 只在认证成功且不在LoginPage的loading状态时查询
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['user-projects-oauth-v2'], // Use same key as Header to share cache
    queryFn: async () => {
      const { apiRequest } = await import('@/lib/queryClient');
      return apiRequest('/api/autodesk/user-projects-with-subscription');
    },
    enabled: isAuthenticated && isHomePage && !isLoading,
  });

  // 检测OAuth回调状态和错误处理
  useEffect(() => {
    const url = new URL(window.location.href);
    const hasSessionId = url.searchParams.has('sessionId') || localStorage.getItem('sessionId');
    const error = url.searchParams.get('error');
    const message = url.searchParams.get('message');
    const isLogout = url.searchParams.get('logout') === 'true';
    const isCleared = url.searchParams.get('cleared') === 'true';
    const isForceClean = url.searchParams.get('force') === 'clean';
    const isForceLogout = url.searchParams.get('force') === 'logout';
    
    // 如果是logout后的重定向，确保完全清理
    if (isLogout || isCleared || isForceClean || isForceLogout) {
      console.log('🔐 LOGIN: Detected logout redirect, ensuring complete cleanup');
      
      // 强制清理所有存储
      localStorage.clear();
      sessionStorage.clear();
      
      // 清理所有cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // 清理URL参数
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('logout');
      cleanUrl.searchParams.delete('cleared');
      cleanUrl.searchParams.delete('force');
      cleanUrl.searchParams.delete('t');
      cleanUrl.searchParams.delete('sessionId');
      window.history.replaceState({}, '', cleanUrl.toString());
      
      console.log('🔐 LOGIN: Complete cleanup finished');
    }
    
    // 处理OAuth错误
    if (error) {
      console.error('🔐 LOGIN: OAuth error detected:', error, message);
      
      // 保持loading状态，但显示错误信息
      setIsLoading(true);
      setProgress(90);
      
      if (error === 'timeout') {
        setLoadingMessage('Authentication is taking longer than expected. Please wait...');
      } else if (error === 'network') {
        setLoadingMessage('Network connection issue. Retrying...');
      } else {
        setLoadingMessage('Authentication in progress. Please wait...');
      }
      
      // 清理URL参数
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('error');
      cleanUrl.searchParams.delete('message');
      window.history.replaceState({}, '', cleanUrl.toString());
      
      // 5秒后自动重试或重置
      setTimeout(() => {
        if (isLoading) {
          console.log('🔐 LOGIN: Resetting after OAuth error');
          setIsLoading(false);
          setProgress(0);
          setLoadingMessage('');
        }
      }, 5000);
      
      return;
    }
    
    if (hasSessionId && !isLoading) {
      console.log('🔐 LOGIN: Detected OAuth callback, starting project loading progress');
      setIsLoading(true);
      setProgress(0);
      setLoadingMessage('Loading projects...');
    }
  }, [isLoading]);

  // 基于真实状态更新progress
  useEffect(() => {
    // Add timeout for authentication check (15 seconds)
    let authTimeout: NodeJS.Timeout | null = null;
    
    if (isLoading) {
      // Set timeout to redirect to login if authentication takes too long
      if (!isAuthenticated) {
        authTimeout = setTimeout(() => {
          console.log('🔐 LOGIN: Authentication timeout, redirecting to login');
          setIsLoading(false);
          setProgress(0);
          setLoadingMessage('');
          // Clear auth-related storage
          localStorage.removeItem('sessionId');
          localStorage.removeItem('autodeskSessionId');
          localStorage.removeItem('loginFlowStarted');
          // Redirect to login
          window.location.href = '/login';
        }, 15000); // 15 seconds timeout
      }
      
      if (isAuthenticated) {
        // Clear timeout if authenticated
        if (authTimeout) {
          clearTimeout(authTimeout);
        }
        
        if (isLoadingProjects) {
          // 认证成功，项目正在加载
          setProgress(50);
          setLoadingMessage('Authentication successful, loading projects...');
        } else if (projectsData) {
          // 认证成功，项目加载完成
          console.log('🔐 LOGIN: Projects loaded successfully, completing progress');
          setProgress(100);
          setLoadingMessage('Projects loaded successfully!');
          
          // 延迟一下让用户看到100%完成，然后让App处理路由
          setTimeout(() => {
            console.log('🔐 LOGIN: Progress complete, letting App handle routing');
            setIsLoading(false);
          }, 1500); // 增加延迟时间，确保用户看到100%
        } else {
          // 认证成功，但项目数据还未加载
          setProgress(75);
          setLoadingMessage('Preparing application...');
        }
      } else {
        // 还未认证成功
        setProgress(25);
        setLoadingMessage('Validating authentication...');
      }
    } else {
      // 如果isLoading为false但还在显示progress，重置状态
      if (progress > 0) {
        console.log('🔐 LOGIN: Loading stopped, resetting progress');
        setProgress(0);
        setLoadingMessage('');
      }
      // Clear timeout if loading stopped
      if (authTimeout) {
        clearTimeout(authTimeout);
      }
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (authTimeout) {
        clearTimeout(authTimeout);
      }
    };
  }, [isLoading, isAuthenticated, isLoadingProjects, projectsData, progress]);

  const handleLogin = () => {
    console.log('🔐 LOGIN: User clicked login button - starting cleanup');
    
    // 检查是否需要强制清除Autodesk OAuth状态
    const needsForceLogout = localStorage.getItem('autodesk_oauth_remembered') === 'true';
    
    if (needsForceLogout) {
      console.log('🔐 FORCE LOGOUT: 检测到Autodesk OAuth状态需要强制清除');
      // 重定向到强制logout页面
      window.location.href = '/force-logout.html';
      return;
    }
    
    // 点击登录时才清理用户信息
    if (typeof window !== 'undefined') {
      console.log('🔐 LOGIN: Clearing all authentication data...');
      
      // 清理所有认证相关的数据
      const authKeys = [
        'sessionId',
        'loginFlowStarted', 
        'adminToken',
        'adminUser',
        'tendererAuth',
        'acc_token',
        'acc_refresh_token', 
        'acc_user_data',
        'selectedProject',
        'hub-data-updated'
      ];
      
      // 清理localStorage
      authKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // 清理sessionStorage
      authKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
      
      // 清理所有cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      // 清理浏览器缓存
      if (window.caches) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      
      // 最终清理
      localStorage.clear();
      sessionStorage.clear();
      
      console.log('🔐 LOGIN: Authentication data cleared, starting login process');
    }
    
    // 开始登录流程
    setIsLoading(true);
    setProgress(10);
    setLoadingMessage('Preparing authentication...');
    
    // 设置登录流程标记
    localStorage.setItem('loginFlowStarted', 'true');
    console.log('🔐 LOGIN: Set loginFlowStarted = true');
    
    // Simulate progress updates
    const progressSteps = [
      { progress: 25, message: 'Connecting to Autodesk...' },
      { progress: 50, message: 'Validating credentials...' },
      { progress: 75, message: 'Setting up session...' },
      { progress: 90, message: 'Redirecting...' }
    ];
    
    progressSteps.forEach((step, index) => {
      setTimeout(() => {
        setProgress(step.progress);
        setLoadingMessage(step.message);
        console.log(`🔐 LOGIN: Progress ${step.progress}% - ${step.message}`);
      }, (index + 1) * 500);
    });
    
    // Start OAuth after brief delay
    setTimeout(() => {
      console.log('🔐 LOGIN: Starting OAuth redirect');
      oauthLogin();
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Mobile-first vertical layout */}
      <div className="flex flex-col min-h-screen">
        
        {/* Top section - Title and Login card */}
        <div className="px-4 lg:px-8 py-6 lg:py-8">
          {/* Brand title above login box */}
          <div className="text-center mb-4 lg:mb-6">
            <div className="flex items-center justify-center">
              <img 
                src="/app-logo.png" 
                alt="Tectonix" 
                className="h-16 w-auto sm:h-20 lg:h-24 object-contain drop-shadow-lg"
                style={{ filter: 'brightness(0)' }}
              />
            </div>
          </div>

          <div className="w-full max-w-md mx-auto">
            <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm rounded-2xl ring-1 ring-gray-200/50">
              <CardHeader className="text-center space-y-4 pb-6">
                <div className="flex justify-center">
                  <Badge className="bg-green-100 text-green-800 border-green-200 px-3 py-1 text-xs sm:text-sm inline-block">
                    Construction Cloud Platform
                  </Badge>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  <span className="block">Professional</span>
                  <span className="text-blue-600 block font-light whitespace-nowrap text-xl sm:text-2xl lg:text-3xl">Procurement Management</span>
                </h2>
              </CardHeader>
              
              <CardContent className="space-y-6 pt-0">
                {/* Quick Access section - visible on all screen sizes */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4 border border-blue-100">
                    <div className="mb-2">
                      <span className="font-medium text-gray-900 text-lg">Quick Access</span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Enable seamless OAuth authentication by signing in with your Autodesk Construction Cloud account
                    </p>
                  </div>
                </div>

                {/* Login button - more prominent on mobile */}
                {(isLoading || forceLoading) ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center gap-3 mb-3">
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                        <span className="font-medium text-blue-900 text-sm lg:text-base">
                          {externalLoadingMessage || loadingMessage || 'Loading...'}
                        </span>
                      </div>
                      <Progress value={externalProgress || progress} className="h-2" />
                      <p className="text-xs text-blue-600 mt-2">{externalProgress || progress}% complete</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <Button 
                      onClick={handleLogin}
                      className="px-12 py-4 h-14 lg:h-16 min-w-[200px] bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 text-base lg:text-lg transform hover:scale-[1.05] hover:-translate-y-1 active:scale-[0.98] flex items-center justify-center gap-3"
                    >
                      Login to ACC
                      <ArrowRight className="w-5 h-5 lg:w-6 lg:h-6" />
                    </Button>
                  </div>
                )}

                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-2">
                    By signing in, you agree to our{" "}
                    <a 
                      href="/terms" 
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms of service
                    </a>
                    {" "}and{" "}
                    <a 
                      href="/privacy" 
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      privacy policy
                    </a>
                  </p>
                  <p className="text-xs text-gray-400">
                    Tenderer access available{" "}
                    <a 
                      href="/tenderer" 
                      className="text-blue-600 hover:text-blue-800 underline font-medium"
                    >
                      here
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom section - Features only (compact) */}
        <div className="flex items-start justify-center p-4 lg:p-8 pt-1 lg:pt-2">
          <div className="w-full max-w-2xl mx-auto text-center">
            <div className="space-y-2 lg:space-y-3">
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed max-w-lg mx-auto mb-4">
                Streamline your construction procurement processes with intelligent tender package management
              </p>
              
              {/* Features grid - more compact, 2x2 on small screens */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-3xl mx-auto">
                <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3 p-3 rounded-xl hover:bg-white/50 transition-colors duration-200">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center shadow-md">
                    <Package className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg">Smart Tender</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">Intelligent package management</p>
                  </div>
                </div>
                
                <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3 p-3 rounded-xl hover:bg-white/50 transition-colors duration-200">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center shadow-md">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg">4-Tier Access</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">Role-based permissions</p>
                  </div>
                </div>
                
                <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3 p-3 rounded-xl hover:bg-white/50 transition-colors duration-200">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center shadow-md">
                    <Building2 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg">Autodesk</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">Construction Cloud integration</p>
                  </div>
                </div>
                
                <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3 p-3 rounded-xl hover:bg-white/50 transition-colors duration-200">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center shadow-md">
                    <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base lg:text-lg">Analytics</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">Real-time insights</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}